import React from "react";

import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";

import TopBarOverview from "@/ui/molecules/TopBarOverview";
import GridLoader from "@/ui/molecules/GridLoader";

import ErrorMessage from "@/ui/atoms/ErrorMessage";
import LayoutFillParentCentered from "@/ui/layouts/LayoutFillParentCentered";

import type { TResponseGetCoaDefinitionByCoaId } from "@/apis/odin/apiODIN";
import GridEditChartOfAccounts from "@/ui/organisms/GridEditChartOfAccounts.tsx/gridEditChartOfAccounts";
import type { TAccountDTO } from "@/store/accounts/accountTypes";
import { Button, Modal, Tooltip } from "@proda-ai/ui-kit";
import LayoutFillParentWithFooter from "@/ui/layouts/LayoutFillParentWithFooter";
import Typography from "@/ui/atoms/Typography";
import { isNullable } from "@/utils/guards";
import { runAccountValidation } from "./utils";

export type ViewEditChartOfAccountsDetailsParams = {
    useState: ({ coaId }: { coaId: number }) => {
        allAccounts: TAccountDTO[] | null;
        allAccountsRequestStatus: "idle" | "pending" | "rejected" | "resolved";
        accountsToBeDeleted: number[];
        isGlobalAdmin: boolean;
        isProdaUser: boolean;
        coaDefinition: TResponseGetCoaDefinitionByCoaId | null;
        coaDefinitionRequestStatus:
            | "idle"
            | "pending"
            | "rejected"
            | "resolved";
        accountsEdited: boolean;
    };
    useCases: () => {
        goBack: (params: { coaId: string }) => void;
        onDiscardCoA: (coaId: number) => Promise<void>;
        addAccount: (account: TAccountDTO) => void;
        updateAccount: (account: TAccountDTO) => void;
        deleteAccount: (accountId: number) => void;
        restoreAccount: (accountId: number) => void;
        onSaveAccounts: (coaId: number) => Promise<void>;
        resetAccounts: (coaId: number) => Promise<void>;
        onCompleteCoA: (coaId: number) => Promise<void>;
        onClickUpload: (coaId: number) => void;
        onPaste: (event: ClipboardEvent) => void;
        navigateToChartsOfAccountsEditMappings: (params: {
            coaId: string;
        }) => void;
    };
};

export type ViewEditChartOfAccountsDetailsProps = BoxProps & {
    coaId: number;
    onClickBackButton: () => void;
};

export const createViewEditChartOfAccountsDetails = ({
    useState,
    useCases,
}: ViewEditChartOfAccountsDetailsParams) => {
    const ViewEditChartOfAccountsDetails = ({
        coaId,
        onClickBackButton,
        ...props
    }: ViewEditChartOfAccountsDetailsProps) => {
        const {
            allAccounts,
            allAccountsRequestStatus,
            coaDefinition,
            accountsEdited,
            accountsToBeDeleted,
            coaDefinitionRequestStatus,
        } = useState({ coaId });
        const {
            goBack,
            addAccount,
            onSaveAccounts,
            updateAccount,
            deleteAccount,
            restoreAccount,
            onCompleteCoA,
            resetAccounts,
            onDiscardCoA,
            onPaste,
            onClickUpload,
            navigateToChartsOfAccountsEditMappings,
        } = useCases();

        const [validationErrors, setValidationErrors] = React.useState<Record<
            number,
            Record<string, string>
        > | null>(null);

        const [showValidationErrors, setShowValidationErrors] =
            React.useState(false);

        const [showCompleteCoAModal, setShowCompleteCoAModal] =
            React.useState(false);

        const [showDiscardCoAModal, setShowDiscardCoAModal] =
            React.useState(false);

        const [showCoAUploadWarningModal, setShowCoAUploadWarningModal] =
            React.useState(false);

        const [showDraftWarningModal, setShowDraftWarningModal] =
            React.useState(false);

        const onClickBackButtonClicked = () => {
            if (isCoADraft) {
                setShowDraftWarningModal(true);
            } else {
                goBack({
                    coaId: String(coaId),
                });
            }
        };

        const handleClickCoAUpload = () => {
            if (allAccounts?.length === 0) {
                onClickUpload(coaId);
            } else {
                setShowCoAUploadWarningModal(true);
            }
        };

        const isLoading =
            allAccountsRequestStatus === "idle" ||
            coaDefinitionRequestStatus === "idle" ||
            allAccountsRequestStatus === "pending" ||
            coaDefinitionRequestStatus === "pending";
        const isError =
            allAccountsRequestStatus === "rejected" ||
            coaDefinitionRequestStatus === "rejected";
        const isCoADraft = coaDefinition !== null && coaDefinition.isDraft;
        const [rowId, setRowId] = React.useState<number>(1);

        const onAddAccount = React.useCallback(() => {
            addAccount({
                id: rowId,
                account: "",
                code: "",
                type: "income",
                section: "",
                subcategory: null,
                includeInSummarizedDocument: false,
                displayOrder: 0,
                mappedFrom: [],
                isEdited: true,
                draft: true,
            });
            setRowId(rowId + 1);
        }, [addAccount, rowId]);

        const rowData = React.useMemo(() => {
            return [...(allAccounts ?? [])].filter(
                account =>
                    !(
                        accountsToBeDeleted.includes(account.id) &&
                        account.draft === true
                    ),
            );
        }, [allAccounts, accountsToBeDeleted]);

        const validateAccounts = React.useCallback(
            (considerNewAccounts: boolean = false) => {
                return runAccountValidation(
                    considerNewAccounts,
                    rowData,
                    validationErrors ?? {},
                    setValidationErrors,
                    setShowValidationErrors,
                );
            },
            [
                rowData,
                validationErrors,
                setValidationErrors,
                setShowValidationErrors,
            ],
        );

        const onSaveAccountsClicked = async () => {
            const hasErrors = validateAccounts(true);
            if (hasErrors) {
                setShowValidationErrors(true);
                return;
            }
            if (!isCoADraft) {
                return;
            }
            if (coaDefinition === null) {
                return;
            }
            await onSaveAccounts(coaDefinition.coaId);
        };

        const gridElement = React.useMemo(() => {
            return (
                <Box sx={{ height: "100%", width: "100%" }}>
                    {isLoading && <GridLoader />}
                    {isError && (
                        <ErrorMessage
                            sx={{
                                marginTop: "80px",
                                marginX: "auto",
                            }}
                        >
                            Error loading chart of accounts details
                        </ErrorMessage>
                    )}
                    {!isLoading && !isError && coaDefinition && (
                        <Box
                            className="grid-edit-chart-of-accounts"
                            sx={{
                                height: "100%",
                                width: "100%",
                            }}
                        >
                            <GridEditChartOfAccounts
                                showValidationErrors={showValidationErrors}
                                coaDefinition={coaDefinition}
                                rowData={rowData}
                                onAddAccount={onAddAccount}
                                updateAccount={updateAccount}
                                deleteAccount={deleteAccount}
                                restoreAccount={restoreAccount}
                                onEditAccounts={validateAccounts}
                                isEditable={
                                    coaDefinition !== null &&
                                    coaDefinition.isDraft
                                }
                                errors={validationErrors ?? {}}
                                accountsToBeDeleted={accountsToBeDeleted}
                            />
                        </Box>
                    )}
                </Box>
            );
        }, [
            isError,
            isLoading,
            rowData,
            showValidationErrors,
            validationErrors,
            accountsToBeDeleted,
            coaDefinition,
            onAddAccount,
            updateAccount,
            deleteAccount,
            restoreAccount,
            validateAccounts,
        ]);

        React.useEffect(() => {
            if (accountsEdited) {
                const handleBeforeUnload = (event: BeforeUnloadEvent) => {
                    event.preventDefault();
                };
                window.addEventListener("beforeunload", handleBeforeUnload);
                return () => {
                    window.removeEventListener(
                        "beforeunload",
                        handleBeforeUnload,
                    );
                };
            }
            return () => {};
        }, [accountsEdited, isCoADraft, allAccounts, coaDefinition, rowData]);

        React.useEffect(() => {
            const handlePaste = (event: ClipboardEvent) => {
                onPaste(event);
            };

            window.addEventListener("paste", handlePaste);
            return () => {
                window.removeEventListener("paste", handlePaste);
            };
        }, [onPaste]);

        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    width: "100%",
                    ...(props.sx || {}),
                }}
                {...props}
            >
                <TopBarOverview
                    title={coaDefinition?.name}
                    backButtonLabel="Back to Charts of Accounts dashboard"
                    onBackButtonClick={onClickBackButtonClicked}
                />
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: "16px",
                        padding: "16px",
                    }}
                >
                    {isCoADraft && !isLoading && !isError && (
                        <Button
                            size="medium"
                            style={{
                                width: "100px",
                            }}
                            onClick={() => {
                                handleClickCoAUpload();
                            }}
                        >
                            Upload CoA
                        </Button>
                    )}
                </Box>
                <LayoutFillParentWithFooter
                    FooterProps={{
                        sx: {
                            backgroundColor: "var(--proda-white)",
                        },
                    }}
                    footerContent={
                        !isNullable(coaDefinition) && (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    flexDirection: "row",
                                    padding: "16px",
                                    width: "100%",
                                    gap: "16px",
                                }}
                            >
                                {isCoADraft ? (
                                    <Button
                                        pattern="secondary"
                                        isGhost={true}
                                        isInline={true}
                                        size={"large"}
                                        leadingIcon={"chevron-left"}
                                        onClick={() =>
                                            goBack({
                                                coaId: String(coaId),
                                            })
                                        }
                                    >
                                        Back to editing Definition
                                    </Button>
                                ) : (
                                    <Box></Box>
                                )}
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        alignItems: "center",
                                        flexDirection: "row",
                                        gap: "16px",
                                    }}
                                >
                                    {isCoADraft && accountsEdited && (
                                        <Tooltip
                                            content={
                                                <Typography variant="body2">
                                                    {!accountsEdited
                                                        ? "No changes to reset"
                                                        : "Reset the chart of accounts to the initial state."}
                                                </Typography>
                                            }
                                        >
                                            <Button
                                                size="large"
                                                onClick={() =>
                                                    void resetAccounts(
                                                        coaDefinition.coaId,
                                                    )
                                                }
                                                pattern="secondary"
                                            >
                                                Reset
                                            </Button>
                                        </Tooltip>
                                    )}
                                    {isCoADraft && accountsEdited && (
                                        <Tooltip
                                            content={
                                                <Typography variant="body2">
                                                    {isCoADraft
                                                        ? !accountsEdited
                                                            ? "No changes to save"
                                                            : "Save the chart of accounts"
                                                        : "CoA is already complete"}
                                                </Typography>
                                            }
                                        >
                                            <Button
                                                size="large"
                                                onClick={() =>
                                                    void onSaveAccountsClicked()
                                                }
                                                disabled={
                                                    !isCoADraft ||
                                                    !accountsEdited
                                                }
                                            >
                                                Save
                                            </Button>
                                        </Tooltip>
                                    )}
                                    {isCoADraft ? (
                                        <>
                                            <Button
                                                size="large"
                                                onClick={() =>
                                                    setShowDiscardCoAModal(true)
                                                }
                                                pattern="danger"
                                                disabled={!isCoADraft}
                                            >
                                                Discard CoA
                                            </Button>
                                            <Tooltip
                                                content={
                                                    <Typography variant="body2">
                                                        {accountsEdited
                                                            ? "You have unsaved changes"
                                                            : allAccounts?.length ===
                                                                0
                                                              ? "You must have at least one account"
                                                              : "Complete the chart of accounts"}
                                                    </Typography>
                                                }
                                            >
                                                <Button
                                                    size="large"
                                                    onClick={() =>
                                                        setShowCompleteCoAModal(
                                                            true,
                                                        )
                                                    }
                                                    disabled={
                                                        accountsEdited ||
                                                        allAccounts?.length ===
                                                            0
                                                    }
                                                >
                                                    Complete CoA
                                                </Button>
                                            </Tooltip>
                                        </>
                                    ) : (
                                        <Button
                                            size="large"
                                            onClick={() =>
                                                navigateToChartsOfAccountsEditMappings(
                                                    {
                                                        coaId: String(coaId),
                                                    },
                                                )
                                            }
                                        >
                                            Edit Mappings
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        )
                    }
                >
                    <LayoutFillParentCentered>
                        {showCompleteCoAModal && (
                            <Modal
                                open={showCompleteCoAModal}
                                headerTitle="Complete CoA?"
                                onClose={() => setShowCompleteCoAModal(false)}
                                onCancel={() => setShowCompleteCoAModal(false)}
                                onConfirm={() => {
                                    if (!isNullable(coaDefinition)) {
                                        void onCompleteCoA(coaDefinition.coaId);
                                    }
                                }}
                            >
                                Completing the CoA will disallow editing
                                accounts.
                            </Modal>
                        )}
                        {showDiscardCoAModal && (
                            <Modal
                                open={showDiscardCoAModal}
                                headerTitle="Discard CoA?"
                                onClose={() => setShowDiscardCoAModal(false)}
                                onCancel={() => setShowDiscardCoAModal(false)}
                                onConfirm={() => {
                                    if (!isNullable(coaDefinition)) {
                                        void onDiscardCoA(coaDefinition.coaId);
                                    }
                                }}
                            >
                                This will delete the Chart of Account draft.
                            </Modal>
                        )}
                        {showCoAUploadWarningModal && (
                            <Modal
                                open={showCoAUploadWarningModal}
                                headerTitle="Upload CoA?"
                                onClose={() =>
                                    setShowCoAUploadWarningModal(false)
                                }
                                onCancel={() =>
                                    setShowCoAUploadWarningModal(false)
                                }
                                onConfirm={() => {
                                    setShowCoAUploadWarningModal(false);
                                    onClickUpload(coaId);
                                }}
                            >
                                <Typography variant="body2">
                                    Uploading a new CoA will overwrite existing
                                    accounts
                                </Typography>
                            </Modal>
                        )}
                        {showDraftWarningModal && (
                            <Modal
                                open={showDraftWarningModal}
                                headerTitle="Are you sure you want to close?"
                                onClose={() => setShowDraftWarningModal(false)}
                                onCancel={() => setShowDraftWarningModal(false)}
                                onConfirm={() => {
                                    setShowDraftWarningModal(false);
                                    onClickBackButton();
                                }}
                            >
                                <Typography variant="body2">
                                    Closing now will save this Chart of Accounts
                                    and any progress completed so far as a
                                    &apos;Draft&apos;.
                                </Typography>
                            </Modal>
                        )}
                        {gridElement}
                    </LayoutFillParentCentered>
                </LayoutFillParentWithFooter>
            </Box>
        );
    };

    return ViewEditChartOfAccountsDetails;
};
